// Design tokens for the Nerobotti assistant panel.
//
// A factory whose every argument is required, for the reason spelled out at the
// top of chatTokens.js: darkTheme and lightTheme are both built as
// merge({}, classicThemeVariables, {...}), so a token defined only in classic is
// silently inherited by the other two. An argument list turns "which themes have
// to supply this" into a call-site error instead of a colour nobody chose.
//
// ## Why the panel needs its own surface at all
//
// It used to paint itself with component.defaultBackgroundColor — the same
// colour as the app behind it — and reach into palette.action.selected and
// palette.primary.main for everything else. On classic that put a translucent
// white bubble and a #1687af button on a #147ba4 background, both a shade away
// from invisible. ActionCard's link already documents the same trap and works
// around it locally. These tokens fix it properly: each theme names a panel
// surface distinct from its own background, and every accent is then chosen
// against that surface rather than against whatever the app happens to be.
//
// ## The user bubble flips polarity between themes
//
// Deliberately. A bubble has to clear 3:1 against the surface it sits on while
// its text clears 4.5:1 against the bubble, and on the two dark themes there is
// no blue dark enough to satisfy the first and light enough to satisfy the
// second. So the dark themes get a pale bubble with dark text and the light
// theme gets a deep blue one with white text. That is why the colour and its
// foreground are one argument pair — they are only correct together.

/** Nerobotti tokens for theme.component.*. Every argument is required. */
export const nerobottiComponentTokens = ({
  surface,
  surfaceRaised,
  border,
  userBubble,
  userBubbleText,
  cardBackground,
  cardBorder,
  accent,
  accentText,
  link,
  muted,
  danger,
  success,
}) => ({
  nerobottiSurface: { backgroundColor: surface },
  nerobottiSurfaceRaised: { backgroundColor: surfaceRaised },
  nerobottiBorder: { borderColor: border },
  nerobottiUserBubble: { backgroundColor: userBubble, color: userBubbleText },
  nerobottiCard: { backgroundColor: cardBackground, borderColor: cardBorder },
  // Filled accents only — the confirm and send buttons. It clears 3:1 against
  // the surface, which is the floor for a control, not the 4.5:1 a run of text
  // needs. That is what `link` is for.
  nerobottiAccent: { backgroundColor: accent, color: accentText },
  nerobottiLink: { color: link },
  nerobottiMuted: { color: muted },
  // Status colours, for accent bars and icons only. They clear 3:1 against the
  // surface — the floor for a graphical object — and deliberately not the 4.5:1
  // a sentence needs, because nothing here says anything in colour alone: a
  // failure is a red rule beside text that is still the normal colour.
  nerobottiDanger: { color: danger },
  nerobottiSuccess: { color: success },
});
